<template>
	<div>
		<div class="d-flex my-2 gap-3 align-items-center">
			<MultipleSelectDropdown
				text="Types"
				:options="watchedResourceTypes"
				@toggle="toggleResourceWatched"
			/>

			<div class="form-check">
				<input
					class="form-check-input"
					type="checkbox"
					v-model="showFullDomain"
					id="show-full-domain-checkbox"
				/>
				<label class="form-check-label" for="show-full-domain-checkbox">
					Show domain
				</label>
			</div>

			<button
				class="ms-auto btn btn-sm btn-outline-secondary"
				@click="handleExport()"
			>
				Export
			</button>

			<ColumnFilterDropdown
				:options="filterOptions"
				:filters="filters"
				@new-filter="handleAddFilter"
				@delete="deleteFilter"
				@toggle="setFilterEnabled($event.index, $event.enabled)"
			/>

			<MultipleSelectDropdown
				text="Columns"
				:options="columns"
				@toggle="toggleColumn"
			/>
		</div>
		<table class="table table-bordered">
			<thead class="table-light text-center">
				<tr>
					<th
						scope="col"
						v-show="isColumnEnabled('sitemap')"
						title="Sitemap"
					>
						Sitemap
					</th>
					<th
						scope="col"
						v-show="isColumnEnabled('type')"
						title="Resource type"
					>
						Type
					</th>
					<th
						scope="col"
						v-show="isColumnEnabled('status')"
						title="Server response code"
					>
						Status
					</th>
					<th
						scope="col"
						v-show="isColumnEnabled('duration')"
						title="Request duration"
					>
						<div class="d-inline-flex gap-1 align-items-center">
							<span>Duration</span>
							<SortingToggle
								:ascending="isAscendingColumn('duration')"
								@toggle="setSortingOrder('duration', $event)"
							/>
						</div>
					</th>
					<th
						scope="col"
						:style="{ width: '30%' }"
						v-show="isColumnEnabled('url')"
						title="URL"
					>
						URL
					</th>
					<th
						scope="col"
						:style="{ width: '30%' }"
						v-show="isColumnEnabled('source')"
						title="Source page of the resource"
					>
						Source
					</th>
					<th
						scope="col"
						:style="{ width: '10%' }"
						v-show="isColumnEnabled('anchor')"
						title="Anchor text of the resource"
					>
						Anchor
					</th>
					<th scope="col" title="Anchor text of the resource">
						Controls
					</th>
				</tr>
			</thead>
			<tbody>
				<ResourceResultRow
					v-for="result in results.slice().reverse()"
					:columnsBitmask="columnsBitmask"
					:result="result"
					:showFullDomain="showFullDomain"
					:key="result.id"
				/>
			</tbody>
		</table>
	</div>
</template>

<script lang="ts">
import {
	ArrayFilter,
	ComparableType,
	restoreFilters,
	saveFilters,
	STRING_COMPARISON_OPERATORS,
	useArrayFilters,
} from "@/hooks/useArrayFilters";
import { useResultsSorting } from "@/hooks/useResultsSorting";
import { TableColumn, useTableColumns } from "@/hooks/useTableColumns";
import { hiddenErrorsService } from "@/services/hiddenErrorsService";
import {
	ResourceResult,
	resourceWatcherService,
} from "@/services/resourceWatcherService";
import { Result } from "@/services/watcherService";
import { TableColumnFilterOptions } from "@/types";
import { isErrorStatusCode, localStorageGetBoolean } from "@/utils";
import { exportTable } from "@/utils/export";
import { ResourceType } from "@/utils/watcher";
import { computed, ComputedRef, defineComponent, ref, watch } from "vue";
import ColumnFilterDropdown, {
	NewArrayFilter,
} from "./ColumnFilterDropdown.vue";
import MultipleSelectDropdown, {
	SelectDropdownOption,
} from "./MultipleSelectDropdown.vue";
import ResourceResultRow from "./ResourceResultRow.vue";
import SortingToggle from "./SortingToggle.vue";

export default defineComponent({
	components: {
		ResourceResultRow,
		MultipleSelectDropdown,
		ColumnFilterDropdown,
		SortingToggle,
	},
	setup() {
		const {
			resourceResults,
			isResourceTypeWatched,
			setIsResourceTypeWatched,
		} = resourceWatcherService;

		// whether to show the full domain of the URL or just a pathname
		const showFullDomain = ref(
			localStorageGetBoolean("resourceResultsShowFullDomain", false)
		);
		watch(showFullDomain, (newValue: boolean) => {
			localStorage["resourceResultsShowFullDomain"] = newValue;
		});

		const initialColumns: (TableColumn & TableColumnFilterOptions)[] = [
			{ value: "sitemap", title: "Sitemap", enabled: true },
			{
				value: "type",
				title: "Type",
				enabled: true,
				filteringEnabled: true,
				comparisonOperators: STRING_COMPARISON_OPERATORS,
			},
			{
				value: "status",
				title: "Status",
				enabled: true,
				filteringEnabled: true,
			},
			{
				value: "duration",
				title: "Duration",
				enabled: true,
				filteringEnabled: true,
			},
			{ value: "url", title: "URL", enabled: true },
			{ value: "source", title: "Source", enabled: true },
			{
				value: "anchor",
				title: "Anchor",
				enabled: true,
				filteringEnabled: true,
				comparisonOperators: STRING_COMPARISON_OPERATORS,
			},
		];
		const { columns, isColumnEnabled, toggleColumn, columnsBitmask } =
			useTableColumns(initialColumns);

		const watchedResourceTypes: ComputedRef<SelectDropdownOption[]> =
			computed(() => {
				const resourceTypeToName: Partial<
					Record<ResourceType, string>
				> = {
					[ResourceType.image]: "Images",
					[ResourceType.css]: "CSS",
					[ResourceType.script]: "Scripts",
					[ResourceType.link]: "Links",
				};
				return Object.entries(isResourceTypeWatched.value).map(
					([type, isWatched]) => ({
						value: type,
						title: resourceTypeToName[type as ResourceType] || type,
						enabled: Boolean(isWatched),
					})
				);
			});
		const toggleResourceWatched = (index: number) => {
			const { value: type, enabled } = watchedResourceTypes.value[index];
			setIsResourceTypeWatched(type as ResourceType, !enabled);
		};

		// result filters
		const filterOptions = initialColumns.filter(col =>
			Boolean(col.filteringEnabled)
		);
		const {
			filters,
			addFilter,
			deleteFilter,
			setFilterEnabled,
			applyFilters,
		} = useArrayFilters();

		// override filters getters for custom fields
		const columnGetterOverrides: Record<string, ArrayFilter["getter"]> = {
			duration: (result: Result) => result.durationMS / 1000,
		};
		const getGetterForKey = (key: string) =>
			columnGetterOverrides[key] ||
			((result: ResourceResult) => {
				const value = result[
					key as keyof ResourceResult
				] as ComparableType | null;
				return value === null ? "" : value;
			});
		const handleAddFilter = (newFilter: NewArrayFilter) => {
			const getter = getGetterForKey(newFilter.key);
			addFilter({
				...newFilter,
				getter,
				enabled: newFilter.enabled ?? true,
			});
		};

		// save/restore filters from localStorage
		for (const filter of restoreFilters("resourceFilters")) {
			handleAddFilter(filter);
		}
		watch(filters.value, (newFilters: ArrayFilter[]) => {
			saveFilters(newFilters, "resourceFilters");
		});

		//  filter results
		const filteredResults = computed(() =>
			applyFilters(resourceResults.value, filters.value)
		);

		// sort results
		const { setSortingOrder, isAscendingColumn, applySorting } =
			useResultsSorting<ResourceResult>(columnGetterOverrides);
		const sortedResults = computed(() =>
			applySorting(filteredResults.value)
		);

		// filter hidden errors
		const { hiddenErrors } = hiddenErrorsService;
		const visibleResults = computed(() =>
			sortedResults.value.filter(
				result =>
					!(
						isErrorStatusCode(result.status) &&
						hiddenErrors.value.has(result.url)
					)
			)
		);

		const handleExport = () => {
			exportTable(columns.value, visibleResults.value, {
				...columnGetterOverrides,
				source: (result: ResourceResult) =>
					`"${result.sources.map(source => source.url).join("\n")}"`,
				anchor: (result: ResourceResult) =>
					`"${result.sources
						.map(source => source.anchor)
						.join("\n")}"`,
			});
		};

		return {
			results: visibleResults,

			showFullDomain,

			columns,
			columnsBitmask,
			isColumnEnabled,
			toggleColumn,

			watchedResourceTypes,
			toggleResourceWatched,

			filters,
			filterOptions,
			handleAddFilter,
			deleteFilter,
			setFilterEnabled,

			setSortingOrder,
			isAscendingColumn,

			handleExport,
		};
	},
});
</script>
